import { t } from '../i18n/languageStore.js';

const KNOWN_ERROR_KEYS = {
  file_too_large: 'apiErrors.file_too_large',
  unsupported_language: 'apiErrors.unsupported_language',
  permission_denied: 'apiErrors.permission_denied',
  authentication_failed: 'apiErrors.authentication_failed',
  validation_error: 'apiErrors.validation_error',
  invalid_idempotency_key: 'apiErrors.invalid_idempotency_key',
  invalid_share_expiry: 'apiErrors.invalid_share_expiry',
  tenant_conflict: 'apiErrors.tenant_conflict',
  dispatcher_required: 'apiErrors.dispatcher_required',
  feature_not_enabled: 'apiErrors.feature_not_enabled',
  efactura_anaf_invalid_query: 'apiErrors.efactura_anaf_invalid_query',
  efactura_anaf_invalid_reference: 'apiErrors.efactura_anaf_invalid_reference',
  efactura_anaf_rate_limited: 'apiErrors.efactura_anaf_rate_limited',
  efactura_anaf_not_configured: 'apiErrors.efactura_anaf_not_configured',
  efactura_anaf_unauthorized: 'apiErrors.efactura_anaf_unauthorized',
  efactura_anaf_unavailable: 'apiErrors.efactura_anaf_unavailable',
  efactura_anaf_invalid_archive: 'apiErrors.efactura_anaf_invalid_archive',
  efactura_anaf_pdf_unavailable: 'apiErrors.efactura_anaf_pdf_unavailable',
  not_acceptable: 'apiErrors.not_acceptable',
  billing_request_too_large: 'apiErrors.billing_request_too_large',
  billing_pdf_not_found: 'apiErrors.billing_pdf_not_found',
  billing_pdf_not_ready: 'apiErrors.billing_pdf_not_ready',
  billing_pdf_unavailable: 'apiErrors.billing_pdf_unavailable',
  billing_pdf_rate_limited: 'apiErrors.billing_pdf_rate_limited',
  billing_collections_not_enabled: 'apiErrors.billing_collections_not_enabled',
  billing_collection_invalid_invoice: 'apiErrors.billing_collection_invalid_invoice',
  billing_collection_active_storno: 'apiErrors.billing_collection_active_storno',
  billing_collection_exceeds_balance: 'apiErrors.billing_collection_exceeds_balance',
  billing_collection_invalid_receipt_series: 'apiErrors.billing_collection_invalid_receipt_series',
  billing_collection_reconciliation_required: 'apiErrors.billing_collection_reconciliation_required',
  billing_invoice_attachment_confirmation_required: 'apiErrors.billing_invoice_attachment_confirmation_required',
  billing_invoice_changed: 'apiErrors.billing_invoice_changed',
  billing_invalid_invoice_status: 'apiErrors.billing_invalid_invoice_status',
  billing_reconciliation_required: 'apiErrors.billing_reconciliation_required',
  freight_managed_invoice_immutable: 'apiErrors.freight_managed_invoice_immutable',
  billing_operation_in_progress: 'apiErrors.billing_operation_in_progress',
  billing_idempotency_key_required: 'apiErrors.billing_idempotency_key_required',
  billing_idempotency_conflict: 'apiErrors.billing_idempotency_conflict',
  billing_invoice_has_collections: 'apiErrors.billing_invoice_has_collections',
  billing_copy_result_deleted: 'apiErrors.billing_copy_result_deleted',
  problems_access_denied: 'apiErrors.problems_access_denied',
  not_found: 'apiErrors.not_found',
  idempotency_content_conflict: 'apiErrors.idempotency_content_conflict',
  share_unavailable: 'apiErrors.share_unavailable',
  rate_limited: 'apiErrors.rate_limited',
  rate_limit_unavailable: 'apiErrors.rate_limit_unavailable',
  service_unavailable: 'apiErrors.service_unavailable',
  invalid_share_code: 'apiErrors.invalid_share_code',
  maintenance_baseline_required: 'apiErrors.maintenance_baseline_required',
  maintenance_can_unavailable: 'apiErrors.maintenance_can_unavailable',
  maintenance_calendar_interval_conflict: 'apiErrors.maintenance_calendar_interval_conflict',
  maintenance_invalid_filter: 'apiErrors.maintenance_invalid_filter',
  maintenance_object_already_associated: 'apiErrors.maintenance_object_already_associated',
  maintenance_service_already_exists: 'apiErrors.maintenance_service_already_exists',
  maintenance_cycle_locked: 'apiErrors.maintenance_cycle_locked',
  maintenance_completion_locked: 'apiErrors.maintenance_completion_locked',
  maintenance_historical_service_use_source: 'apiErrors.maintenance_historical_service_use_source',
  maintenance_intervention_delete_forbidden: 'apiErrors.maintenance_intervention_delete_forbidden',
  maintenance_sync_in_progress: 'apiErrors.maintenance_sync_in_progress',
  maintenance_refresh_throttled: 'apiErrors.maintenance_refresh_throttled',
  maintenance_limiter_unavailable: 'apiErrors.maintenance_limiter_unavailable',
  maintenance_due_required: 'apiErrors.maintenance_due_required',
  maintenance_interval_required: 'apiErrors.maintenance_interval_required',
  maintenance_type_required: 'apiErrors.maintenance_type_required',
};

const FIELD_ERROR_KEYS = {
  required: 'apiErrors.field_required',
  invalid: 'apiErrors.invalid',
};

export const getApiFieldErrors = (payload) => {
  const fields = payload?.params?.fields;
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return {};

  return Object.fromEntries(Object.entries(fields).map(([field, value]) => {
    const codes = Array.isArray(value) ? value : [value];
    return [field, codes.map((code) => t(FIELD_ERROR_KEYS[code] || 'apiErrors.invalid'))];
  }));
};

const firstMessage = (value) => {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(firstMessage).filter(Boolean).join(' · ');
  if (value && typeof value === 'object') {
    return Object.entries(value)
      .map(([field, message]) => {
        const normalized = firstMessage(message);
        return normalized ? `${field}: ${normalized}` : '';
      })
      .filter(Boolean)
      .join(' · ');
  }
  return '';
};

export const parseRetryAfter = (value, now = Date.now()) => {
  if (!value) return 0;

  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds));

  const retryDate = Date.parse(value);
  if (!Number.isFinite(retryDate)) return 0;
  return Math.max(0, Math.ceil((retryDate - now) / 1000));
};

export const getApiErrorMessage = (payload, fallback = t('apiErrors.request_failed')) => {
  if (!payload || typeof payload !== 'object') return fallback;

  const code = typeof payload.code === 'string'
    ? payload.code
    : typeof payload.error === 'string'
      ? payload.error
      : '';
  if (code && KNOWN_ERROR_KEYS[code]) {
    const params = { ...(payload.params || {}) };
    if (params.fields && typeof params.fields === 'object') params.fields = getApiFieldErrors(payload);
    return t(KNOWN_ERROR_KEYS[code], params);
  }
  if (typeof payload.detail === 'string' && payload.detail) return payload.detail;
  const reason = firstMessage(payload.reason);
  if (code && reason) return `${code}: ${reason}`;
  if (reason) return reason;

  return firstMessage(
    payload.message ||
    payload.error ||
    payload.errors ||
    payload.non_field_errors
  ) || firstMessage(payload) || fallback;
};

export const createApiError = (response, payload, fallback) => {
  const error = new Error(getApiErrorMessage(payload, fallback));
  error.name = 'ApiError';
  error.status = response?.status;
  error.code = payload?.code || payload?.error || null;
  error.reason = payload?.reason || null;
  error.details = payload || null;
  error.retryAfter = parseRetryAfter(response?.headers?.get?.('Retry-After'));
  return error;
};

export const readApiError = async (response, fallback) => {
  const contentType = response?.headers?.get?.('content-type') || '';
  let payload = null;

  if (contentType.includes('json')) {
    payload = await response.json().catch(() => null);
  } else {
    const message = await response.text().catch(() => '');
    payload = message ? { message: message.slice(0, 500) } : null;
  }

  return createApiError(response, payload, fallback);
};
